import { Article, FeatureArticle } from '../../Elements/Articles';
import {
  FeatureArticleWrapper,
  FilterButtonsContainer,
  FilterContainer,
  PaginatorWrapper,
  SearchInputWrapper,
  SearchedTermWrapper,
  articleGrid,
  galleryCSS,
  noResultDescription,
  noResultImage,
} from './styles/SectionFilterableArticle.styles';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import {
  BreadcrumbItems,
  ContentfulFilters,
  CustomWindow,
  FilterList,
  IArticle,
  ObjectEventType,
  SectionFilterableArticleProps,
  SeoDataTypes,
  TagCategory,
  TagTopic,
} from './types/SectionFilterableArticles.types';
import React, {
  Key,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { withPrefix } from 'gatsby';
import Heading from '@legacy-vfuk/core-heading';
import { Helmet } from 'react-helmet';
import Paginator from '@legacy-vfuk/core-paginator';
// import SWBreadcrumb from '../SWBreadcrumb/SWBreadcrumb';
import SimpleGrid from '@legacy-vfuk/core-simple-grid';
import { StructuredData } from '../../../SEO/StructuredData';
import { css } from 'styled-components';
import setupBreadcrumbAnalitycsTags from '../../../../helpers/setupBreadcrumbs';
import useSiteMetadata from '../../../../hooks/useSiteMetadata';
import {
  SimpleGridWrapper,
  FilterWrapper,
  CardsWrapper,
} from '../SectionFeature/styles/SectionFeatureRow.styles';
import FeatureCardsWithIcon from '../../Elements/FeatureCardsWithIcon/FeatureCardsWithIcon';
import { Button } from '@legacy-vfuk/core-button';
import { SearchInput } from '@legacy-vfuk/core-search-input';
import DrawerFilter from './DrawerFilter';
import { useDebounce } from '../../../../hooks/useDebounce';
import { DirectionContext } from '../../../../contexts/DirectionContext';
import {
  clearDisplayAsFeatured,
  compareByFeatured,
  compareByViewCount,
} from '../../../../helpers/articleHelpers';

// The size is overwritten from the contentful.
let PAGE_SIZE_FIRST = 9;
let PAGE_SIZE = 8;
const MOBILE_VIEW_WIDTH = 794;

declare const window: CustomWindow;

const SectionFilterableArticles = ({
  section,
  location,
}: SectionFilterableArticleProps) => {
  const {
    slug: articleIndexSlug,
    name,
    seoTitle,
    seoDescription,
    articleIndex,
    homePageName,
    homePageUrl,
    externalPageName,
    externalPageUrl,
    uiTextNewestFilterButton,
    featuredContentType,
    uiTextMostPopularFilterButton,
    uiTextAllFilterButton,
    uiTextFilterByFilterButton,
    uiTextFilterByCategories,
    uiTextFilterApplied,
    uiTextTimeFilterLabel,
    uiTextContentTypeLabel,
    uiTextCategoriesSubCategories,
    uiTextCancelFilterButton,
    uiTextApplyFilterButton,
    uiTextFilteredTerm,
    uiTextSearchDefault,
    uiTextSearchedTerm,
    uiTextCategoriesDropdownDefault,
    uiTextTopicsDropdownDefault,
    uiTextNoResults,
    uiTextNoResultsDescription,
    uiImageNoResultsFound,
    uiTextLinkClear,
    mainCategoryCards,
    mainCategories,
    tagCategories,
    tagTopics,
    filterTime,
    filterContentType,
    filterSeoEventData,
    pageSizeFirst,
    pageSize,
    mostPopularEnabled,
  } = section;
  // New Design Update: Usign categories var to merge tagCategories and mainCategories,
  // instead just using tag Categories.
  const categories = mainCategories ? mainCategories.slice() : [];
  tagCategories && categories.push(...tagCategories);
  const isMobile = checkWindowSize();

  const { siteUrl } = useSiteMetadata();
  const direction = useContext(DirectionContext);

  PAGE_SIZE_FIRST = pageSizeFirst ? Number(pageSizeFirst) : PAGE_SIZE_FIRST;
  PAGE_SIZE = pageSize ? Number(pageSize) : PAGE_SIZE;
  const paramsObj = new URLSearchParams(location.search);

  const querySearchString = paramsObj.get('query_search')
    ? decodeURIComponent(paramsObj.get('query_search'))
    : '';

  // NB - queryCategoryString will be either a string value or empty value.
  // It will correspond to the value part of the dropdown objects.
  const queryCategoryString = paramsObj.get('query_category')
    ? decodeURIComponent(paramsObj.get('query_category'))
    : '';
  // The selected category dropdown label value.
  const categoryDisplayName = getTagNameFromValue(
    categories,
    queryCategoryString
  );

  const queryTopicString = paramsObj.get('query_topic')
    ? decodeURIComponent(paramsObj.get('query_topic'))
    : '';

  const topicDisplayName = getTagNameFromValue(tagTopics, queryTopicString);

  const queryTimeRangeString = paramsObj.get('query_time_range')
    ? decodeURIComponent(paramsObj.get('query_time_range'))
    : '';

  const queryContentTypeString = paramsObj.get('query_content_type')
    ? decodeURIComponent(paramsObj.get('query_content_type'))
    : '';

  const queryPageString = paramsObj.get('query_page')
    ? decodeURIComponent(paramsObj.get('query_page'))
    : '1';

  // Form category dropdown options.
  const categoryOptions = useRef(
    (categories || []).map(tagCategory => ({
      text: tagCategory.displayName,
      value: tagCategory.slugName,
    }))
  );

  if (categoryOptions.current[0].text !== uiTextCategoriesDropdownDefault) {
    categoryOptions.current.unshift({
      text: uiTextCategoriesDropdownDefault,
      value: '',
      topics: [],
    });
  }

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Setting up breadcrumbs analytics
  useEffect(() => {
    setupBreadcrumbAnalitycsTags();
  }, []);

  const onNewestClick = () => {
    if (filterList.newest) {
      return;
    }

    clearFilterState();

    setSeoData({
      event_type: 'newest',
      filter_query: 'newest',
    });

    setFilterList(prevFilterList => ({ ...prevFilterList, newest: true }));
  };

  const onFeaturedArticleClick = () => {
    if (isFeaturedArticleApplied()) {
      return;
    }

    clearFilterState();

    setFilterList(prevFilterList => ({
      ...prevFilterList,
      contentType: [featuredContentType],
    }));

    setSeoData({
      event_type: 'content_type',
      filter_query: featuredContentType.slugName,
    });
  };

  const isFeaturedArticleApplied = () => {
    return filterList.contentType.find(
      appliedContentType => appliedContentType.name === featuredContentType.name
    );
  };

  const onMostPopularClick = () => {
    if (filterList.mostPopular) {
      return;
    }

    clearFilterState();

    setSeoData({
      event_type: 'most_popular',
      filter_query: 'most_popular',
    });

    setFilterList(prevFilterState => ({
      ...prevFilterState,
      mostPopular: true,
    }));
  };

  // Form topic dropdown options.
  const rawTopicOptions = (tagTopics || []).map(
    (tagTopic): TagTopic => ({
      text: tagTopic.displayName,
      value: tagTopic.slugName,
    })
  );
  rawTopicOptions.unshift({ text: uiTextTopicsDropdownDefault, value: '' });
  const [topicOptions, setTopicOptions] = useState(rawTopicOptions);

  const fullTopicOptions = useRef([]);
  // The currently selected category and topic dropdown values.

  const hasHydrated = useRef(false);

  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItems>({
    renderItems: [],
    seoItems: [],
  });

  const [seoData, setSeoData] = useState({
    event_type: 'category',
    filter_query: queryCategoryString,
  });

  const [page, setPage] = useState(
    queryPageString ? Number(queryPageString) - 1 : 0
  );

  const [filterList, setFilterList] = useState<FilterList>({
    category: categoryDisplayName,
    categoryValue: queryCategoryString,
    topic: topicDisplayName,
    topicValue: queryTopicString,
    selectedTopics: getTagsAndFiltersFromValues(
      tagTopics,
      queryTopicString.split(',')
    ),
    newest: false,
    page: page,
    mostPopular: false,
    timeRange: getTagsAndFiltersFromValues(
      filterTime,
      queryTimeRangeString.split(',')
    ),
    contentType: getTagsAndFiltersFromValues(
      filterContentType,
      queryContentTypeString.split(', ')
    ),
  });

  const [searchQuery, setSearchQuery] = useState(querySearchString);
  const debouncedSearchQuery = useDebounce(searchQuery);

  const [{ content: contentData }] = articleIndex;
  const [{ content }] = contentData;
  const [ContentfulSectionFilterableArticleIndex] = content;
  // The unfiltered articles. Note that the articles are rendered on hydration, so
  // articles is populated on the useEffect(() => {}, []).
  const articles = useRef([]);
  // The filtered article list (i.e. after the search query and category/topic
  // filters are applied.
  const filteredArticles = formFilteredContent(
    articles.current,
    filterList,
    debouncedSearchQuery,
    categories
  );
  // Paged version of filteredArticles.
  const [pagedArticles, setPagedArticles] = useState(
    formPagedContent(filteredArticles, isMobile)
  );
  // The article list as a view.
  const currentPageContent: IArticle[] = pagedArticles[page] || [];

  const list = useMemo(() => {
    return currentPageContent.map(article => {
      const isFeatured = article.displayAsFeatured;

      if (isFeatured) {
        return (
          <FeatureArticleWrapper
            className="featured-article-grid"
            key={article.id}
          >
            <FeatureArticle
              key={article.slug}
              indexSlug={articleIndexSlug}
              backgroundColor={'white'}
              data={article}
              seoTitle={seoTitle}
              appliedFilters={seoData}
              queryCategories={filterList.topic.split(',')}
              viewCount={article.viewCount}
            />
          </FeatureArticleWrapper>
        );
      } else {
        return (
          <Article
            key={article.slug}
            indexSlug={articleIndexSlug}
            backgroundColor={'white'}
            data={article}
            seoTitle={seoTitle}
            appliedFilters={seoData}
            queryCategories={filterList.topic.split(',')}
            viewCount={article.viewCount}
          />
        );
      }
    });
  }, [currentPageContent]);

  function onFilterChange() {
    const currentPage = page + 1;

    updateUrlQueryParams('query_category', filterList.categoryValue);

    updateUrlQueryParams('query_topic', filterList.topicValue);

    const timeRanges = filterList.timeRange
      .map(time => time.slugName)
      .join(',');

    updateUrlQueryParams('query_time_range', timeRanges);

    const contentTypes = filterList.contentType
      .map(contentTypeFilter => contentTypeFilter.slugName)
      .join(',');

    updateUrlQueryParams('query_content_type', contentTypes);

    updateUrlQueryParams('query_page', filterList.page + 1, currentPage === 1);

    if (currentPage !== 1 && currentPage !== filterList.page) {
      setPage(filterList.page);
    }
  }

  useEffect(() => {
    updateUrlQueryParams('query_search', debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    onFilterChange();
  }, [filterList]);

  useEffect(() => {
    if (
      categoryDisplayName === filterList.category &&
      topicDisplayName === filterList.topic &&
      Number(queryPageString) === page
    )
      return;

    const newFilter = {
      ...filterList,
      category: categoryDisplayName,
      categoryValue: queryCategoryString,
      topic: topicDisplayName,
      topicValue: queryTopicString,
      page: queryPageString ? Number(queryPageString) - 1 : 0,
    };

    categoryOptions.current.some(category => {
      const isFound = categoryDisplayName === category.text;
      return isFound;
    });

    topicOptions.some(topic => {
      const isFound = topicDisplayName === topic.displayName;
      return isFound;
    });

    if (Number(queryPageString) !== page) {
      setPage(newFilter.page);
    }

    setFilterList(newFilter);
  }, [location.search]);

  function checkWindowSize() {
    const [isMobile, setIsMobile] = useState(false);
    if (typeof window !== 'undefined') {
      useEffect(() => {
        function handleResize() {
          if (window.innerWidth <= MOBILE_VIEW_WIDTH) {
            setIsMobile(true);
          }
        }
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
      }, []);
    }
    return isMobile;
  }

  // Update the filterList from the Category dropdown field
  function onCategoryFilterChange(
    e: React.ChangeEvent<HTMLSelectElement> | ObjectEventType
  ) {
    // Handling default component event
    const firstOption = categoryOptions.current[0];
    const target = e.target ? e.target : e;
    const selectedCategory =
      target === undefined
        ? firstOption
        : categoryOptions.current.find(
            category => category.value === target.value
          );
    const newFilterList: FilterList = {
      ...filterList,
      category: selectedCategory?.text || '',
      categoryValue: selectedCategory?.value || '',
      mostPopular: false,
      newest: false,
    };

    setSeoData({
      event_type: 'category',
      filter_query: newFilterList.category,
    });
    setFilterList(newFilterList);
  }

  useEffect(() => {
    const newFilter = { ...filterList, page: page };
    setFilterList(newFilter);
  }, [page]);

  const firePaginatorClickEvent = (targetValue: number) => {
    try {
      // fire the utag link event
      if (window.onPaginatorClickEvent) {
        window.onPaginatorClickEvent(targetValue);
      }
    } catch (e) {
      console.log(
        'onPaginatorClickEvent failed - please check the Page Changing Arrows Script'
      );
    }
  };

  const changePage = newPage => {
    firePaginatorClickEvent(newPage);
    setPage(newPage - 1);
    window.scrollTo(0, 0);
  };

  const runFilterEventData = (sData: SeoDataTypes) => {
    const filterEventData = filterSeoEventData.filterSeoEventData;

    if (window.onFilterUserEvent && filterEventData) {
      try {
        const payload = JSON.parse(filterEventData);
        const { type, data } = payload;

        // Fire the filterUserEvent
        window.onFilterUserEvent(type, { ...data, ...sData });
      } catch (e) {
        console.log(
          'onFilterUserEvent failed - please check the Filter SEO script JSON and that a SeoInteractionScript is correctly set up.'
        );
      }
    }
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;

    setSeoData({
      event_type: 'search',
      search_terms: searchValue,
    });

    setSearchQuery(searchValue);
    setPage(0);
  };

  const updateUrlQueryParams = (
    name: string,
    value: string | number,
    shouldDeleteFromUrl?: boolean
  ) => {
    const url = new URL(window.location.href);

    if (value && !shouldDeleteFromUrl) {
      url.searchParams.set(name, value.toString());
    } else {
      url.searchParams.delete(name);
    }

    if (
      window.history.state?.data !== url &&
      url.href !== window.location.href
    ) {
      window.history.pushState(
        { action: 'filterChange', data: url.href },
        '',
        url
      );
    }

    return url;
  };

  useEffect(() => {
    const results = formFilteredContent(
      articles.current,
      filterList,
      debouncedSearchQuery,
      categories
    );

    setPagedArticles(formPagedContent(results, isMobile));

    // SEO Tracking is allowed for the opco.
    runFilterEventData({
      ...seoData,
      search_results_count: results.length,
    });
  }, [debouncedSearchQuery, filterList, seoData]);

  useEffect(() => {
    // Effect helpers
    function makeTopics(topics = [], uiTextTopicsDropdownDefault) {
      const theTopics = topics.map(
        (topic: {
          name?: string;
          slugName?: string;
          displayName?: string;
        }) => ({
          text: topic.displayName || topic.name,
          value: topic.slugName,
        })
      );
      // Add default
      theTopics.unshift({
        text: uiTextTopicsDropdownDefault,
        value: '',
      });
      return theTopics;
    }

    // Add the articles.
    articles.current = ContentfulSectionFilterableArticleIndex.articles;

    // Form category dropdown options.
    categoryOptions.current = (categories || []).map(tagCategory => ({
      text: tagCategory.displayName,
      value: tagCategory.slugName,
      topics: makeTopics(tagCategory.topics, uiTextTopicsDropdownDefault),
    }));
    /*
    categoryOptions.current.unshift({
      label: uiTextCategoriesDropdownDefault,
      value: null,
      topics: [],
    });
    */
    // form full topic options
    fullTopicOptions.current = (tagTopics || []).map(tagTopic => ({
      text: tagTopic.displayName,
      value: tagTopic.slugName,
    }));
    fullTopicOptions.current.unshift({
      text: uiTextTopicsDropdownDefault,
      value: '',
    });
    // On initial page load, set the default category/topics by simulating a
    // user generated change event matching the initial conditions.
    const e = { text: categoryDisplayName, value: queryCategoryString };
    onCategoryFilterChange(e);
  }, []);

  // Remove the visually hidden h2 Element, embedded in the Breadcrumb component
  useEffect(() => {
    const elems = Array.from(document.getElementsByTagName('h2'));
    elems.forEach(elem => {
      if (elem.className === 'visually-hidden') elem.remove();
    });
  });

  const isFiltersReset = (): boolean => {
    const ignoreKeys = ['page'] as const satisfies Array<keyof FilterList>;
    type RelevantInResetFilterListKeys = Exclude<
      keyof FilterList,
      (typeof ignoreKeys)[number]
    >;

    const filterListKeys: RelevantInResetFilterListKeys[] = Object.keys(
      filterList
    ).filter(
      (key): key is RelevantInResetFilterListKeys =>
        !ignoreKeys.includes(key as never)
    );

    return (
      filterListKeys.every(key => {
        const filterListValue = filterList[key];

        if (Array.isArray(filterListValue)) {
          return filterListValue.length < 1;
        }

        return !filterListValue;
      }) && debouncedSearchQuery === ''
    );
  };

  const clearFilterState = () => {
    setTopicOptions(rawTopicOptions);
    setSearchQuery('');

    const clearFilterValues: FilterList = {
      category: '',
      categoryValue: '',
      topic: '',
      topicValue: '',
      selectedTopics: [],
      contentType: [],
      timeRange: [],
      newest: false,
      mostPopular: false,
      page: 0,
    };

    setFilterList(prevFilterList => ({
      ...prevFilterList,
      ...clearFilterValues,
    }));

    setSeoData({
      event_type: 'clear',
      filter_query: '',
    });
  };

  const onAllButtonClick = () => {
    if (isFiltersReset()) {
      return;
    }
    clearFilterState();
  };

  // Set flag to true, when the DOM has re-rendered at least once
  useEffect(() => {
    hasHydrated.current = true;
  }, []);

  const DisplayListOfArticles = ({ list }: { list: IArticle[] }) => {
    if (hasHydrated.current && list.length === 0) {
      return (
        <>
          {uiImageNoResultsFound && (
            <GatsbyImage
              image={getImage(uiImageNoResultsFound)}
              css={noResultImage}
              alt="No results found"
            />
          )}
          <Heading level={5} justify="center">
            <b>{uiTextNoResults}</b>
          </Heading>
          <p css={noResultDescription}>{uiTextNoResultsDescription}</p>
        </>
      );
    }

    return <div css={articleGrid}>{list}</div>;
  };

  const runBreadcrumbEventData = async sData => {
    if (window.onBreadcrumbsUserEvent && sData) {
      try {
        const finished = await window.onBreadcrumbsUserEvent(
          'sectionFilterableArticles',
          {
            ...sData,
          }
        );
      } catch (e) {
        console.log(
          'onBreadcrumbsUserEvent failed - please check the Navigation SEO script JSON and that a SeoInteractionScript is correctly set up.'
        );
      }
    }
  };

  const onClickHandler = async (e, href) => {
    e.preventDefault();
    await runBreadcrumbEventData({ location: 'sectionFilterableArticles' });
    window.location.href = href;
  };

  useEffect(() => {
    setBreadcrumbItems(createBreadCrumbItems());
  }, [
    externalPageName,
    externalPageUrl,
    homePageName,
    name,
    siteUrl,
    location.pathname,
  ]);

  const handleCardClick = (urlLink: string) => {
    const categoryName = getTagNameFromValue(categories, urlLink);
    const e = { text: categoryName, value: urlLink };
    onCategoryFilterChange(e);
  };

  const calculatePlaceholderWidth = () => {
    const placeHolderLength = uiTextSearchDefault.length;
    let placeHolderPixelWidth = placeHolderLength * 5 + 200;
    placeHolderPixelWidth =
      placeHolderPixelWidth < 250 ? 250 : placeHolderPixelWidth;
    return placeHolderPixelWidth + 'px';
  };

  const createBreadCrumbItems = () => {
    const items: BreadcrumbItems = { renderItems: [], seoItems: [] };

    if (externalPageName && externalPageUrl) {
      items.renderItems.push({
        id: 'item-1',
        text: externalPageName,
        href: externalPageUrl,
      });
      items.seoItems.push({
        '@type': 'ListItem',
        position: 1,
        name: externalPageName,
        item: externalPageUrl,
      });
    }

    items.renderItems.push(
      {
        id: 'item-' + (items.renderItems.length + 1),
        text: homePageName,
        href: withPrefix(homePageUrl),
      },
      {
        id: 'item-' + (items.renderItems.length + 2),
        text: name,
      }
    );

    items.seoItems.push(
      {
        '@type': 'ListItem',
        position: items.seoItems.length + 1,
        name: homePageName,
        item: `${siteUrl}${withPrefix(homePageUrl)}`,
      },
      {
        '@type': 'ListItem',
        position: items.seoItems.length + 2,
        name: name,
        item: `${siteUrl}${location.pathname}`,
      }
    );
    return items;
  };

  return (
    <>
      <Helmet>
        {seoTitle.trim().length > 0 && <title>{seoTitle}</title>}
        {seoDescription.trim().length > 0 && (
          <meta name="description" content={seoDescription} />
        )}
      </Helmet>
      <StructuredData>
        {{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbItems.seoItems,
        }}
      </StructuredData>
      {/* TO DO: Remove if none of the OpCos will use the breadcrumb anymore */}
      {/* <div data-testid="breadCrumb-knowledgeCenter" css={breadcrumbWrapper}>
        <SWBreadcrumb
          location="sectionFilterableArticles"
          items={
            breadcrumbItems.renderItems.length > 0
              ? breadcrumbItems.renderItems
              : createBreadCrumbItems().renderItems
          }
        />
            </div> */}

      <CardsWrapper css={galleryCSS}>
        {debouncedSearchQuery !== '' && (
          <SearchedTermWrapper>
            <h3>
              {uiTextSearchedTerm?.replace(
                '{searchedTerm}',
                debouncedSearchQuery
              )}
            </h3>
          </SearchedTermWrapper>
        )}

        {mainCategoryCards &&
          mainCategoryCards?.length > 0 &&
          debouncedSearchQuery === '' && (
            <>
              {filterList.category ? (
                <SearchedTermWrapper>
                  <h3>
                    {uiTextFilteredTerm.replace(
                      '{searchedTerm}',
                      filterList.category
                    )}
                  </h3>
                </SearchedTermWrapper>
              ) : (
                <SimpleGridWrapper size="large">
                  <SimpleGrid
                    columns={{
                      sm: 1,
                      lg: mainCategoryCards.length,
                    }}
                    spacing={7}
                    verticalAlign="stretch"
                    justify="center"
                  >
                    {mainCategoryCards.map(card => {
                      return (
                        <FeatureCardsWithIcon
                          key={card.id}
                          card={card}
                          onCardClick={handleCardClick}
                        />
                      );
                    })}
                  </SimpleGrid>
                </SimpleGridWrapper>
              )}
            </>
          )}
      </CardsWrapper>

      <FilterWrapper css={galleryCSS}>
        <FilterContainer>
          <FilterButtonsContainer>
            <Button
              text={uiTextNewestFilterButton}
              appearance={filterList.newest ? 'secondary' : 'alt1'}
              onClick={() => onNewestClick()}
            />
            {featuredContentType && (
              <Button
                text={featuredContentType.displayName}
                appearance={isFeaturedArticleApplied() ? 'secondary' : 'alt1'}
                onClick={() => onFeaturedArticleClick()}
              />
            )}
            <Button
              text={uiTextMostPopularFilterButton}
              appearance={filterList.mostPopular ? 'secondary' : 'alt1'}
              onClick={() => onMostPopularClick()}
              state={!mostPopularEnabled ? 'disabled' : undefined}
            />
            <Button
              text={uiTextAllFilterButton}
              appearance={isFiltersReset() ? 'secondary' : 'alt1'}
              onClick={() => onAllButtonClick()}
            />
            <Button
              text={uiTextFilterByFilterButton}
              icon={{ name: 'filter', justify: 'right' }}
              appearance="alt2"
              onClick={() => setIsFilterOpen(true)}
            />
          </FilterButtonsContainer>

          <DrawerFilter
            key={isFilterOpen as unknown as Key}
            isOpen={isFilterOpen}
            setIsOpen={setIsFilterOpen}
            tagCategories={categories}
            tagTopics={tagTopics}
            filterTime={filterTime}
            filterContentype={filterContentType}
            filterList={filterList}
            setFilterList={setFilterList}
            uiTextLinkClear={uiTextLinkClear}
            uiTextFilterByCategories={uiTextFilterByCategories}
            uiTextFilterApplied={uiTextFilterApplied}
            uiTextContentTypeLabel={uiTextContentTypeLabel}
            uiTextTimeFilterLabel={uiTextTimeFilterLabel}
            uiTextCategoriesSubCategories={uiTextCategoriesSubCategories}
            uiTextCancelFilterButton={uiTextCancelFilterButton}
            uiTextApplyFilterButton={uiTextApplyFilterButton}
            setSeoData={setSeoData}
          />

          <SearchInputWrapper
            direction={direction}
            inputWidth={calculatePlaceholderWidth()}
          >
            <SearchInput
              textInput={{
                id: 'search',
                value: searchQuery,
                onChange: e => onSearch(e),
                placeholder: uiTextSearchDefault,
              }}
              fieldWrapper={{
                showLabel: false,
                label: '',
                width: 'full',
              }}
              searchIconButton={{ onClick: () => null }} // to suppress default source web onClick behavior
              onClear={() => setSearchQuery('')}
            />
          </SearchInputWrapper>
        </FilterContainer>
      </FilterWrapper>

      <div
        css={css`
          background-color: var(--gallery);
        `}
      >
        {/* Article index grid */}
        <div>
          <DisplayListOfArticles list={list} />
        </div>
        {pagedArticles.length > 0 && (
          <PaginatorWrapper
            data-testid="pagination"
            isSecondLanguageOn={homePageUrl !== '/' && homePageUrl !== null}
          >
            <Paginator
              id="the-paginator"
              numberOfPages={pagedArticles.length}
              currentPage={page + 1}
              onClick={changePage}
            />
          </PaginatorWrapper>
        )}
      </div>

      {/* <Block
        css={css`
          background-color: var(--gallery);
        `}
      >
        <div className="spring">
          <div css={rightAlign}>
            <Link onClick={handleSmoothScroll} text={uiTextBackToTopLabel} />
          </div>
        </div>
      </Block> */}
    </>
  );
};

function formFilteredContent(
  articles: IArticle[],
  filters: FilterList,
  query: string,
  categories: TagCategory[]
) {
  const categoryFilter = filters.category;
  const topicFilter = filters.topic;
  const timeRangeFilter = filters.timeRange;
  const contentTypeFilter = filters.contentType;

  const newestArticle = getNewestArticle(articles);

  let filteredData: IArticle[] = articles.filter(article => {
    let hasCategoryMatch = false;
    let hasTopicMatch = false;
    let hasTimeRangeMatch = false;
    let hasContentTypeMatch = false;
    let hasTitleMatch = false;
    let hasSummaryMatch = false;
    let hasKeywordMatch = false;
    let hasNewestMatch = false;

    if (filters.categoryValue !== '') {
      const foundCategory = categories.find(category => {
        return category.slugName === filters.categoryValue;
      });
      const categoryTopics = foundCategory.topics.map(topic => topic.name);
      // In case the article has the picked Category
      const articleHasCategory = article.categoryTags.some(
        tag => tag.displayName === categoryFilter || tag.name === categoryFilter
      );
      // In case the article has at least one topic that is under the picked Category
      const articleHasCategoryTopic = article.topicTags.some(
        tag =>
          categoryTopics.includes(tag.displayName) ||
          categoryTopics.includes(tag.name)
      );
      hasCategoryMatch = articleHasCategory || articleHasCategoryTopic;
    } else {
      hasCategoryMatch = true;
    }

    if (topicFilter.length !== 0) {
      hasTopicMatch = article.topicTags.some(
        tag =>
          topicFilter.includes(tag.displayName) ||
          topicFilter.includes(tag.name)
      );
    } else {
      hasTopicMatch = true;
    }

    if (timeRangeFilter.length !== 0) {
      const currentTime = new Date();
      hasTimeRangeMatch = timeRangeFilter.some(timeRange => {
        const timeThreshold =
          currentTime.getTime() - timeRange.timeRangeInMinutes * 60 * 1000;
        const articleTime = new Date(article.publishingDate).getTime();
        return articleTime >= timeThreshold;
      });
    } else {
      hasTimeRangeMatch = true;
    }

    if (contentTypeFilter.length !== 0) {
      hasContentTypeMatch = contentTypeFilter.some(contentTypeFilter => {
        const articleHasMatchingContentType =
          article.contentType?.slugName === contentTypeFilter.slugName;
        const articleHasNoContentTypeAndIsDefaultSelected =
          !article.contentType && contentTypeFilter.isDefault;

        return (
          articleHasMatchingContentType ||
          articleHasNoContentTypeAndIsDefaultSelected
        );
      });
    } else {
      hasContentTypeMatch = true;
    }

    if (query.length > 0) {
      hasTitleMatch = article.pageArticleName
        .toLowerCase()
        .includes(query.toLowerCase());

      hasSummaryMatch = article.summary
        .toLowerCase()
        .includes(query.toLowerCase());

      hasKeywordMatch = article.keywords
        .map(keyword => keyword.toLowerCase())
        .includes(query.toLowerCase());
    } else {
      hasTitleMatch = true;
      hasKeywordMatch = true;
    }

    if (filters.newest) {
      hasNewestMatch = article.id === newestArticle.id;
    } else {
      hasNewestMatch = true;
    }

    return (
      hasCategoryMatch &&
      hasTopicMatch &&
      hasTimeRangeMatch &&
      hasContentTypeMatch &&
      hasNewestMatch &&
      (hasTitleMatch || hasSummaryMatch || hasKeywordMatch)
    );
  });

  if (filters.newest) {
    filteredData = filteredData.filter(
      article => article.id === getNewestArticle(filteredData).id
    );
  }

  filteredData = clearDisplayAsFeatured(filteredData);

  if (filters.mostPopular) {
    filteredData = filteredData.sort(compareByViewCount);
    if (filteredData[0]) {
      filteredData[0].displayAsFeatured = true;
    }
  } else {
    filteredData = filteredData.sort(compareByFeatured);
    if (filteredData[0] && filteredData[0].featured) {
      filteredData[0].displayAsFeatured = true;
    }
  }

  return filteredData;
}

function formPageSummary(
  templateString: string,
  hasMultiplePages: boolean,
  firstItemPos: number,
  currentPageContentLength: number,
  filteredArticlesLength: number
) {
  const resultsRange: string = hasMultiplePages
    ? `${firstItemPos + 1}-${currentPageContentLength + firstItemPos}`
    : String(filteredArticlesLength);

  if (filteredArticlesLength !== 0) {
    return templateString
      .replace('{resultsRange}', resultsRange)
      .replace('{totalResults}', String(filteredArticlesLength));
  }
  return '';
}

function formPagedContent(data: any[], isMobile: boolean) {
  const hasFeaturedArticle = data.some((article) => article.featured)

  let pagedData = data.reduce((resultArray, item, index) => {
    let pageSizeFirst = hasFeaturedArticle ? PAGE_SIZE_FIRST : PAGE_SIZE;
    let pageSizeRest = PAGE_SIZE;
    if (isMobile) {
      pageSizeFirst = 3;
      pageSizeRest = 3;
    }
    const isFirstPage = index < pageSizeFirst;
    const pageSize = isFirstPage ? pageSizeFirst : pageSizeRest;
    const pageIndex = isFirstPage
      ? Math.floor(index / pageSize)
      : Math.max(Math.floor((index - pageSizeFirst) / pageSize + 1), 1);

    if (!resultArray[pageIndex]) {
      // start new page
      resultArray[pageIndex] = [];
    }
    resultArray[pageIndex].push(item);
    return resultArray;
  }, []);
  
  if (pagedData.length === 0) {
    pagedData = [[]];
  }
  return pagedData;
}

function getNewestArticle(articles: IArticle[]) {
  const newestArticle = articles.sort((a, b) => {
    return (
      new Date(b.publishingDate).getTime() -
      new Date(a.publishingDate).getTime()
    );
  })[0];

  return newestArticle;
}

function getTagValueFromName(
  tagList: TagCategory[] | TagTopic[],
  tagName: string
) {
  let tagValue = '';
  if (tagName !== '') {
    tagList.some(tag => {
      const isFound = tag.displayName === tagName;
      if (isFound) {
        tagValue = tag.slugName;
      }
      return isFound;
    });
  }
  return tagValue;
}

function getTagNameFromValue(
  tagList: TagCategory[] | TagTopic[],
  tagValue: string
): string {
  const tagValueArray = tagValue.split(',');

  const tagNameArray: string[] = [];

  if (tagValue === '') {
    return '';
  }

  tagValueArray.map(value => {
    tagList.some(tag => {
      if (value.includes(tag.slugName)) {
        tagNameArray.push(tag.displayName);
        return true;
      }
    });
  });

  return tagNameArray.join(',');
}

function getTagsAndFiltersFromValues<T extends ContentfulFilters[number]>(
  tagList: T[],
  tagValues: string[]
): T[] {
  const tagArray: T[] = [];

  if (!tagList) {
    return [];
  }

  if (tagValues.length === 0 || tagList.length === 0) {
    return [];
  }

  tagValues.map(value => {
    tagList.some(tag => {
      if (value.includes(tag.slugName)) {
        tagArray.push(tag);
        return true;
      }
    });
  });

  return tagArray;
}

export default SectionFilterableArticles;
